import PageLayout from '../../../components/PageLayout';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import { Tag, Search, Checkbox } from 'carbon-components-react';
import {
  Edit,
  Users,
  Link as LinkIcon,
  FileText,
  Check,
  Power,
  Trash2,
  Printer,
} from 'react-feather';
import { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import { ConfirmModal } from '../../../components/modal/confirm-modal/ConfirmModal.component';
import { useCustomMutation } from '../../../utils/useCustomMutation';
import './financial-aid.style.scss';
import { deleteMethod, patchMethod } from '../../../api/common';
import {
  formatDate,
  getPersonName,
  getStatusColor,
  isAdmin,
} from '../../../utils/common.util';
import { RenderIf } from '../../../components/RenderIf';
import { PrintView } from '../../../components/PrintView/print-view.component';
import PrintOutHead from '../../../components/PrintOutHead';
import ComponentGuard from '@/components/component-guard/ComponentGuard';
import { BILLING_SUB_ROLES } from '@/utils/user-roles';

// Helper to get badge color based on aid type
const getAidTypeColor = (aidType) => {
  switch (aidType) {
    case 'Scholarship':
      return 'green';
    case 'Discount':
      return 'blue';
    case 'Waiver':
      return 'purple';
    default:
      return 'gray';
  }
};

// Helper to get status color
const getTagColor = (isActive) => {
  if (isActive === true) return 'green';
  return 'gray';
};

export default function SingleFinancialAidPage() {
  const { id } = useParams();
  const history = useHistory();
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);

  // Fetch program data
  const { data: program, isLoading: isLoadingProgram } = useSWR(
    id ? `financial-aid/programs/${id}` : null,
  );

  // Fetch assigned students
  const { data: students, isLoading: isLoadingStudents } = useSWR(
    id ? `financial-aid/programs/${id}/students` : null,
  );

  const isLoading = isLoadingProgram || isLoadingStudents;

  // Transform API data to UI format
  const programData = program
    ? {
        id: program.id,
        programName: program.name,
        aidType: program.type,
        amount: program.amount,
        amountType: program.amountType,
        academicYearId: program.academicYearId,
        academicYearDescription: program.academicYearDescription,
        termId: program.termId,
        termDescription: program.termDescription,
        description: program.description,
        status: program.isActive ? 'Active' : 'Inactive',
        dateCreated: program.createdAt,
        // Metadata fields
        createdBy: getPersonName(program.createdBy),
        createdAt: program.createdAt,
        // approvedBy: getPersonName(program.approvedBy),
        // approvedAt: program.approvedAt,
        deletedBy: getPersonName(program.deletedBy),
        deletedAt: program.deletedAt,
        modifiedBy: getPersonName(program.modifiedBy),
        modifiedAt: program.modifiedAt,
        deactivatedBy: getPersonName(program.deactivatedBy) || 'N/A',
        deactivatedAt: program.deactivatedAt,
        lastUpdatedAt: program.lastUpdatedAt || program.modifiedAt,
      }
    : null;

  // Linked bill items from program.billItems
  const linkedItems = program?.billItems || [];

  // Assigned students from API
  const assignedStudentsData = students || [];

  // Filter students based on search
  const filteredStudents = assignedStudentsData.filter((student) => {
    const searchLower = studentSearch.toLowerCase();
    return (
      student.studentName?.toLowerCase().includes(searchLower) ||
      student.studentUniqueId?.toLowerCase().includes(searchLower)
    );
  });

  // Modal states
  const [isOpen, setIsOpen] = useState('');
  const [error, setError] = useState('');

  // Toggle active mutation
  const { trigger: toggleTrigger, isMutating: isToggling } = useCustomMutation(
    `financial-aid/programs/${id}/toggle-active`,
    (url, { arg }) => patchMethod(url, arg),
  );

  // Delete mutation
  const { trigger: deleteTrigger, isMutating: isDeleting } = useCustomMutation(
    `financial-aid/programs/${id}/delete`,
    (url, { arg }) => deleteMethod(url, arg),
  );

  // Approve students mutation
  const {
    trigger: approveTrigger,
    isMutating: isApproving,
    error: approvalError,
  } = useCustomMutation(
    `financial-aid/programs/${id}/students/approve`,
    (url, { arg }) => patchMethod(url, arg),
  );

  // Check if current user can approve (they didn't assign any of the students)
  // const canApprove = assignedStudentsData.some(
  //   (student) => student.assignedBy?.id !== currentUser?.id,
  // );

  // Get students that can be approved by current user (not assigned by them)
  // const approvableStudents = assignedStudentsData.filter(
  //   (student) => student.assignedBy?.id !== currentUser?.id,
  // );
  const approvableStudents = assignedStudentsData;

  // Handle checkbox change
  const handleStudentCheckbox = (studentId, isChecked) => {
    setSelectedStudentIds((prev) =>
      isChecked ? [...prev, studentId] : prev.filter((id) => id !== studentId),
    );
  };

  // Handle select all
  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      const allApproveableIds = approvableStudents
        .filter((s) => s.approvalStatus !== 'Approved')
        .map((s) => s.studentId);
      setSelectedStudentIds(allApproveableIds);
    } else {
      setSelectedStudentIds([]);
    }
  };

  // Check if all approvable students are selected
  const isAllSelected = approvableStudents
    .filter((s) => s.approvalStatus !== 'Approved')
    .every((s) => selectedStudentIds.includes(s.studentId));

  // Handle approve
  const handleApprove = async () => {
    const payload = {
      studentIds: selectedStudentIds,
    };

    try {
      await approveTrigger(payload, {
        onSuccess: () => {
          setSelectedStudentIds([]);
          mutate(`financial-aid/programs/${id}/students`);
        },
        onError: (err) => {
          setError(err.message || 'Failed to approve students');
        },
      });
    } catch (error) {
      setError(error.message || 'Failed to approve students');
    }
  };

  // Handle toggle active
  const handleToggleActive = async () => {
    try {
      await toggleTrigger(null, {
        onSuccess: () => {
          setIsOpen('');
          mutate(`financial-aid/programs/${id}`);
        },
        onError: (err) => {
          setError(err.message || 'Failed to toggle program status');
        },
      });
    } catch (error) {
      setError(error.message || 'Failed to toggle program status');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteTrigger(null, {
        onSuccess: () => {
          setIsOpen('');
          history.push('/financial-aid');
        },
        onError: (err) => {
          setError(err.message || 'Failed to delete program');
        },
      });
    } catch (error) {
      setError(error.message || 'Failed to delete program');
    }
  };

  // Handle Linked Bill Items h2 click - navigate to step 2 in edit mode
  const handleLinkedItemsClick = () => {
    history.push(`/financial-aid/create?id=${id}&step=2`);
  };

  // Handle Assigned Students h2 click - navigate to step 3 in edit mode
  const handleAssignedStudentsClick = () => {
    history.push(`/financial-aid/create?id=${id}&step=3`);
  };

  // Handle edit button click - navigate to create page with program ID
  const handleEdit = () => {
    history.push(`/financial-aid/create?id=${id}`);
  };

  if (isLoading) {
    return (
      <PageLayout
        title="Loading..."
        breadcrumbs={[
          { label: 'Billing', to: '/billing' },
          { label: 'Financial Aid Programs', to: '/financial-aid' },
          { label: 'Loading...', active: true },
        ]}
        isLoading={true}
      >
        <div>Loading program details...</div>
      </PageLayout>
    );
  }

  if (!programData) {
    return (
      <PageLayout
        title="Program Not Found"
        breadcrumbs={[
          { label: 'Billing', to: '/billing' },
          { label: 'Financial Aid Programs', to: '/financial-aid' },
          { label: 'Not Found', active: true },
        ]}
      >
        <div className="empty-state">
          <p>The requested financial aid program could not be found.</p>
          <CustomButton onClick={() => history.push('/financial-aid')}>
            Back to Programs
          </CustomButton>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={programData.programName}
      breadcrumbs={[
        { label: 'Billing', to: '/billing' },
        { label: 'Financial Aid Programs', to: '/financial-aid' },
        { label: programData.programName, active: true },
      ]}
      isLoading={isLoading}
    >
      <div className="single-aid-page">
        {/* Program Details Card */}
        <div className="aid-details-card">
          <div className="card-header">
            <div className="header-left">
              <FileText size={20} className="header-icon" />
              <h2>Program Details</h2>
            </div>
            <div className="header-right ">
              <ComponentGuard
                allowedRoles={[BILLING_SUB_ROLES.FINANCIAL_AID_ACCESS]}
              >
                <div className="action-buttons d-flex" style={{ gap: '12px' }}>
                  <CustomButton
                    classes="outline font-bold primary"
                    onClick={handleEdit}
                  >
                    <Edit size={16} style={{ marginRight: '8px' }} />
                    Edit Program
                  </CustomButton>
                  <RenderIf condition={isAdmin()}>
                    <CustomButton
                      classes="outline font-bold secondary"
                      onClick={() => setIsOpen('toggle')}
                    >
                      <Power size={16} style={{ marginRight: '8px' }} />
                      {program?.isActive ? 'Deactivate' : 'Activate'}
                    </CustomButton>
                    <CustomButton
                      classes="outline font-bold danger"
                      onClick={() => setIsOpen('delete')}
                    >
                      <Trash2 size={16} style={{ marginRight: '8px' }} />
                      Delete
                    </CustomButton>
                  </RenderIf>
                </div>
              </ComponentGuard>
            </div>
          </div>

          <div className="card-body">
            {/* Quick Stats Row */}
            <div className="stats-row">
              <div className="stat-item">
                <span className="stat-label">Total Students</span>
                <span className="stat-value">
                  <Users size={16} />
                  {program?.studentCount || assignedStudentsData.length || 0}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Bill Items Linked</span>
                <span className="stat-value">
                  <LinkIcon size={16} />
                  {linkedItems.length}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Status</span>
                <span className="stat-value">
                  <Tag type={getTagColor(program?.isActive)}>
                    {programData.status}
                  </Tag>
                </span>
              </div>
            </div>

            <hr className="divider" />

            {/* Program Info Grid */}
            <div className="info-grid card-row-4">
              <div className="info-item">
                <label>Program Name</label>
                <p className="program-name">{programData.programName}</p>
              </div>

              <div className="info-item">
                <label>Aid Type</label>
                <p>
                  <Tag type={getAidTypeColor(programData.aidType)}>
                    {programData.aidType}
                  </Tag>
                </p>
              </div>

              <div className="info-item">
                <label>Amount</label>
                <p className="amount">
                  {programData.amountType === 'Percentage'
                    ? `${programData.amount}%`
                    : `GHS ${parseFloat(programData.amount).toLocaleString()}`}
                </p>
              </div>

              <div className="info-item">
                <label>Academic Year</label>
                <p>{programData.academicYearDescription}</p>
              </div>

              <div className="info-item">
                <label>Term</label>
                <p>{programData.termDescription || 'All Terms'}</p>
              </div>

              <div className="info-item">
                <label>Description</label>
                <p className="description">
                  {programData.description || 'No description provided.'}
                </p>
              </div>

              {/* Metadata Grid */}
              <div className="info-item">
                <label>Created By</label>
                <p>{programData.createdBy || 'N/A'}</p>
              </div>

              <div className="info-item">
                <label>Created Date</label>
                <p>{formatDate(programData.createdAt)}</p>
              </div>

              {/* <div className="info-item">
                <label>Approved By</label>
                <p>{programData.approvedBy || 'N/A'}</p>
              </div>

              <div className="info-item">
                <label>Approved Date</label>
                <p>{formatDate(programData.approvedAt)}</p>
              </div> */}

              <div className="info-item">
                <label>Deleted By</label>
                <p>{programData.deletedBy || 'N/A'}</p>
              </div>

              <div className="info-item">
                <label>Deleted Date</label>
                <p>{formatDate(programData.deletedAt)}</p>
              </div>

              <div className="info-item">
                <label>Modified By</label>
                <p>{programData.modifiedBy || 'N/A'}</p>
              </div>

              <div className="info-item">
                <label>Modified Date</label>
                <p>{formatDate(programData.modifiedAt)}</p>
              </div>

              <div className="info-item">
                <label>Deactivated By</label>
                <p>{programData.deactivatedBy || 'N/A'}</p>
              </div>

              <div className="info-item">
                <label>Deactivated Date</label>
                <p>{formatDate(programData.deactivatedAt)}</p>
              </div>

              <div className="info-item">
                <label>Last Update</label>
                <p>{formatDate(programData.lastUpdatedAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Linked Bill Items Section */}
        <div className="linked-items-section">
          <div className="section-card">
            <div className="section-header">
              <div className="header-left" onClick={handleLinkedItemsClick}>
                <LinkIcon size={20} className="header-icon" />
                <h2>Linked Bill Items</h2>
              </div>
              <span className="item-count">
                {linkedItems.length} items linked
              </span>
            </div>

            <div className="card-body">
              {linkedItems.length > 0 ? (
                <div className="table-container">
                  <table className="bx--data-table">
                    <thead>
                      <tr>
                        <th>Bill Item</th>
                        <th>Bill Amount</th>
                        <th>Override</th>
                        {/* <th>Status</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {linkedItems.map((item) => (
                        <tr key={item.id}>
                          <td>{item.feeDescription}</td>
                          <td>GHS {item.feeAmount?.toLocaleString()}</td>
                          <td>
                            {item.overrideAmount ? (
                              <span className="override-value">
                                {programData.amountType === 'Percentage'
                                  ? `${parseFloat(item.overrideAmount).toLocaleString()}%`
                                  : `GHS ${parseFloat(item.overrideAmount).toLocaleString()}`}
                              </span>
                            ) : (
                              <span className="no-override">
                                Uses program default
                              </span>
                            )}
                          </td>
                          {/* <td>
                            <Tag type="green" size="sm">
                              <Check size={12} style={{ marginRight: '4px' }} />
                              Linked
                            </Tag>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No bill items linked to this program.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Assigned Students Section */}
        <div className="assigned-students-section">
          <div className="section-card">
            <div className="section-header">
              <div
                className="header-left"
                onClick={handleAssignedStudentsClick}
              >
                <Users size={20} className="header-icon" />
                <h2>Assigned Students</h2>
              </div>
              <div className="student-stats">
                <span className="total">
                  Total: {assignedStudentsData.length}
                </span>
                {/* <span className="enrolled">Enrolled: {enrolledCount}</span> */}
              </div>
            </div>

            <div className="card-body">
              {/* Search Bar */}
              <div className=" d-flex-space-between">
                <div className="search-bar" style={{ width: '50%' }}>
                  <Search
                    size={20}
                    placeholder="Search by name or ID..."
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    labelText="Search"
                    id="student-search"
                  />
                </div>

                {/* Action Bar */}
                <ComponentGuard
                  allowedRoles={[
                    BILLING_SUB_ROLES.FINANCIAL_AID_APPROVAL_ACCESS,
                  ]}
                >
                  <div>
                    <CustomButton
                      classes="primary font-bold"
                      onClick={handleApprove}
                      disabled={selectedStudentIds.length === 0 || isApproving}
                      isLoading={isApproving}
                    >
                      <Check size={16} style={{ marginRight: '8px' }} />
                      Approve Selected ({selectedStudentIds.length})
                    </CustomButton>
                  </div>
                </ComponentGuard>
              </div>

              {/* Error display for approval */}
              {approvalError && (
                <p className="text-14 mb-10 text-danger">
                  {approvalError?.message ||
                    'Failed to approve selected students. Please try again.'}
                </p>
              )}

              <PrintOutHead
                title={`Students Assigned to ${programData.programName}`}
              />

              {assignedStudentsData.length > 0 ? (
                <div className="table-container">
                  <PrintView>
                    <table className="bx--data-table">
                      <thead>
                        <tr>
                          <ComponentGuard
                            allowedRoles={[
                              BILLING_SUB_ROLES.FINANCIAL_AID_APPROVAL_ACCESS,
                            ]}
                          >
                            <th style={{ width: '50px' }} className="no-print">
                              <Checkbox
                                id="selectAllStudents"
                                checked={isAllSelected}
                                onChange={(value) => handleSelectAll(value)}
                              />
                            </th>
                          </ComponentGuard>
                          <th>Student ID</th>
                          <th>Name</th>
                          <th>Class</th>
                          <th>Override Amount</th>
                          <th>Effective Amount</th>
                          <th>Approval Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map((student) => {
                          // const isAssignedByCurrentUser =
                          //   student.assignedBy?.id === currentUser?.id;
                          // const canApproveThisStudent = !isAssignedByCurrentUser;
                          return (
                            <tr key={student.id}>
                              <ComponentGuard
                                allowedRoles={[
                                  BILLING_SUB_ROLES.FINANCIAL_AID_APPROVAL_ACCESS,
                                ]}
                              >
                                <td
                                  style={{ width: '50px' }}
                                  className="no-print"
                                >
                                  <Checkbox
                                    id={`student-${student.id}`}
                                    checked={selectedStudentIds.includes(
                                      student.studentId,
                                    )}
                                    onChange={(value) =>
                                      handleStudentCheckbox(
                                        student.studentId,
                                        value,
                                      )
                                    }
                                    disabled={
                                      student.approvalStatus === 'Approved'
                                    }
                                  />
                                </td>
                              </ComponentGuard>
                              <td className="student-id">
                                {student.studentUniqueId}
                              </td>
                              <td className="student-name">
                                {student.studentName}
                              </td>
                              <td className="student-class">{student.class}</td>
                              <td>
                                {student.overrideAmount ? (
                                  <span className="override-value">
                                    {programData.amountType === 'Percentage'
                                      ? `${parseFloat(student.overrideAmount).toLocaleString()}%`
                                      : `GHS ${parseFloat(student.overrideAmount).toLocaleString()}`}
                                  </span>
                                ) : (
                                  <span className="no-override">
                                    {programData.amountType === 'Percentage'
                                      ? `${parseFloat(programData.amount).toLocaleString()}%`
                                      : `GHS ${parseFloat(programData.amount).toLocaleString()}`}
                                  </span>
                                )}
                              </td>
                              <td>
                                <span className="effective-amount">
                                  {programData.amountType === 'Percentage'
                                    ? `${parseFloat(student.effectiveAmount).toLocaleString()}%`
                                    : `GHS ${parseFloat(student.effectiveAmount).toLocaleString()}`}
                                </span>
                              </td>
                              <td
                                className={getStatusColor(
                                  student?.approvalStatus,
                                )}
                              >
                                {student.approvalStatus}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </PrintView>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No students assigned to this program.</p>
                </div>
              )}

              {filteredStudents.length === 0 &&
                assignedStudentsData.length > 0 && (
                  <div className="empty-state">
                    <p>No students found matching your search.</p>
                  </div>
                )}

              {assignedStudentsData?.length > 0 && (
                <div className="mt-30 d-flex gap-20 align-items-center flex-end">
                  <CustomButton
                    classes="secondary"
                    onClick={() => window.print()}
                  >
                    <Printer size="16" strokeWidth="2px" /> &nbsp; Print
                  </CustomButton>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toggle Active Confirm Modal */}
      <ConfirmModal
        isOpen={isOpen === 'toggle'}
        closeModal={() => {
          setIsOpen('');
          setError('');
        }}
        title={program?.isActive ? 'Deactivate Program' : 'Activate Program'}
        actionText={program?.isActive ? 'DEACTIVATE' : 'ACTIVATE'}
        description={
          program?.isActive
            ? 'Are you sure you want to deactivate this program? Students will no longer receive the financial aid benefits until it is reactivated.'
            : 'Are you sure you want to activate this program? Students will start receiving the financial aid benefits.'
        }
        onClick={handleToggleActive}
        isLoading={isToggling}
        actionBtnClasses={program?.isActive ? 'danger' : 'primary'}
        errorMessage={error && isOpen === 'toggle' ? error : null}
      />

      {/* Delete Confirm Modal */}
      <ConfirmModal
        isOpen={isOpen === 'delete'}
        closeModal={() => {
          setIsOpen('');
          setError('');
        }}
        title="Delete Program"
        actionText="DELETE"
        description="Are you sure you want to delete this financial aid program? This action cannot be undone and all associated data will be lost."
        onClick={handleDelete}
        isLoading={isDeleting}
        actionBtnClasses="danger"
        errorMessage={error && isOpen === 'delete' ? error : null}
      />
    </PageLayout>
  );
}
